import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import useTitanApp from "@/hooks/useTitanApp";
import {
  getDrugBySearch,
  setselectedDrugs,
  setDrugSummary,
} from "@/dispatchers/nmsDispatchers";
// ✅ Validation Schema
export const medicationSchema = z.object({
  item: z.string().min(1, "Item is required"),
  dose: z.string().min(1, "Dose is required"),
  quantity: z.string().optional(),
  therapyLevel: z.enum(["acute", "chronic", "maintenance", "asneeded"]),
  notes: z.string().optional(),
});

// ✅ Types
export type MedicationForm = z.infer<typeof medicationSchema>;

export interface MedicationEditMode {
  id: string;
  item: string;
  dose: string;
  times?: {
    morning?: string;
    midday?: string;
    evening?: string;
    night?: string;
  };
  quantity?: string;
  therapyLevel: "acute" | "chronic" | "maintenance" | "asneeded";
  preparation?: string;
  notes?: string;
}

// ✅ Static Data (unchanged)
export const therapyLevelOptions = [
  { value: "chronic", label: "Chronic" },
  { value: "acute", label: "Acute" },
  { value: "maintenance", label: "Maintenance" },
  { value: "asneeded", label: "As Needed" },
];

// ✅ Mapping Helpers
export const therapyLevelNumberToString: Record<
  number,
  "chronic" | "acute" | "maintenance" | "asneeded"
> = {
  1: "chronic",
  2: "acute",
  3: "maintenance",
  4: "asneeded",
};

export const therapyLevelStringToNumber: Record<
  "chronic" | "acute" | "maintenance" | "asneeded",
  number
> = {
  chronic: 1,
  acute: 2,
  maintenance: 3,
  asneeded: 4,
};

// ✅ Normalize backend data when it returns numbers
export const normalizeMedicationData = (data: any[]): MedicationEditMode[] => {
  return data.map((item) => ({
    ...item,
    therapyLevel:
      typeof item.therapyLevel === "number"
        ? therapyLevelNumberToString[item.therapyLevel] ?? "acute"
        : (item.therapyLevel as
            | "acute"
            | "chronic"
            | "maintenance"
            | "asneeded"),
  }));
};

// ✅ Custom Hook for Medication Form State
export const useMedicationFormState = (editMode?: MedicationEditMode) => {
  const [open, setOpen] = useState(false);

  const formMethods = useForm<MedicationForm>({
    resolver: zodResolver(medicationSchema),
    defaultValues: editMode
      ? {
          item: editMode.item,
          dose: editMode.dose,
          quantity: editMode.quantity || "",
          therapyLevel:
            typeof editMode.therapyLevel === "number"
              ? therapyLevelNumberToString[editMode.therapyLevel]
              : editMode.therapyLevel,
          notes: editMode.notes || "",
        }
      : {
          therapyLevel: "acute",
        },
  });

  return {
    open,
    setOpen,
    ...formMethods,
  };
};

// ✅ Convert string → number on submit
export const handleSubmit = (
  data: MedicationForm,
  onSave: (medication: any) => void,
  setOpen: (open: boolean) => void,
  reset: () => void
) => {
  const payload = {
    ...data,
    therapyLevel: therapyLevelStringToNumber[data.therapyLevel],
  };

  onSave(payload);
  setOpen(false);
  reset();
};

export const useMedicationDose = () => {
  const { pharmacyServiceState, nmsServiceState } = useTitanApp();
  const { doseShortCodeList } = pharmacyServiceState || {};
  const [searchLoading, setSearchLoading] = useState(false);
  const {} = useTitanApp();
  const { drugSummary } = nmsServiceState || {};

  const handleDose = (val) => {
    let valueToSet;

    const endsWithSpace = val?.endsWith(" ");

    if (endsWithSpace) {
      const words = val?.trim().split(" ");
      const lastWord = words[words.length - 1];
      const matched = doseShortCodeList?.find(
        (entry) => entry?.shortCode?.toLowerCase() === lastWord.toLowerCase()
      );
      if (matched) {
        words[words.length - 1] = matched.description;
        valueToSet = words.join(" ") + " ";
      } else {
        valueToSet = val;
      }
    } else {
      valueToSet = val;
    }

    return valueToSet;
  };

  const drugSearch = async (keyword: string) => {
    if (!keyword || keyword.trim().length < 2) return;
    setSearchLoading(true);
    try {
      const response = await getDrugBySearch({ Keyword: keyword });
      if (Array.isArray(response)) {
        setselectedDrugs(response);
        setDrugSummary(response);
      }
    } finally {
      setSearchLoading(false);
    }
  };

  return {
    handleDose,
    searchLoading,
    drugSearch,
    drugSummary,
  };
};
